import {
  cloneElement,
  isValidElement,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../cn';

/** Space between the trigger and the tip, and the least space kept to the window edge. */
const GAP = 6;
const EDGE = 8;

export interface TooltipProps {
  /** The trigger. A single element gets `aria-describedby`; anything else is wrapped as-is. */
  children: ReactNode;
  label: ReactNode;
  side?: 'top' | 'bottom' | undefined;
  className?: string | undefined;
}

/**
 * Shows on hover and on keyboard focus. Fully rounded, navy, shadow-4 (design rule).
 * The tip is drawn in `document.body`, so a neighbouring card, a lifted tile or a clipped
 * container cannot cover or cut it. It stays inside the window and flips below the trigger
 * when there is no room above.
 */
export function Tooltip({ children, label, side = 'top', className }: TooltipProps) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const anchor = useRef<HTMLSpanElement>(null);
  const tip = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const a = anchor.current;
    const t = tip.current;
    if (a && t) {
      const r = a.getBoundingClientRect();
      const maxLeft = document.documentElement.clientWidth - t.offsetWidth - EDGE;
      const left = Math.max(EDGE, Math.min(r.left + r.width / 2 - t.offsetWidth / 2, maxLeft));
      const above = r.top - t.offsetHeight - GAP;
      const top = side === 'top' && above >= EDGE ? above : r.bottom + GAP;
      t.style.left = `${String(left)}px`;
      t.style.top = `${String(top)}px`;
    }
    // The tip is placed once per open; scrolling or resizing closes it.
    const close = () => {
      setOpen(false);
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open, side]);

  const trigger = isValidElement<{ 'aria-describedby'?: string }>(children)
    ? cloneElement(
        children as ReactElement<{ 'aria-describedby'?: string }>,
        open ? { 'aria-describedby': id } : {},
      )
    : children;
  return (
    <span
      ref={anchor}
      className={cn('relative inline-flex', className)}
      onMouseEnter={() => {
        setOpen(true);
      }}
      onMouseLeave={() => {
        setOpen(false);
      }}
      onFocus={() => {
        setOpen(true);
      }}
      onBlur={() => {
        setOpen(false);
      }}
    >
      {trigger}
      {open
        ? createPortal(
            <span
              ref={tip}
              role="tooltip"
              id={id}
              className="pointer-events-none fixed top-0 left-0 z-300 max-w-[calc(100vw-16px)] truncate rounded-full bg-surface-action-neutral-default px-2.5 py-1.5 font-display text-xs font-medium whitespace-nowrap text-content-inversed shadow-4"
            >
              {label}
            </span>,
            document.body,
          )
        : null}
    </span>
  );
}
